import { TimezoneDTO } from '@eventbrite/create-common';
import { format } from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';
import type {
    AutoCreateEventFormValues,
    CreateAccountFormValues,
    CreateTransientEventPayload,
    EventServicePayload,
} from '../types';

const DEFAULT_FORMAT = 'yyyy-MM-dd';
const DEFAULT_TIMEZONE = {
    timezone: 'UTC',
    label: 'Coordinated Universal Time',
};

export const toUTC = ({
    date,
    timezone,
}: {
    date: string;
    timezone: string;
}) => {
    const utcDate = zonedTimeToUtc(date, timezone);
    return {
        timezone,
        utc: utcDate.toISOString().replace(/[.]\d+/, ''),
    };
};

export const toPriceWithCurrency = ({
    price,
    currency,
}: {
    price: number;
    currency: string;
}) => {
    return `${currency},${price === 0 ? '000' : Math.floor(price * 100)}`;
};

export const composeDateTime = (date: string, time: string) =>
    `${date}T${time}`;

export const createTransientEventPayload = ({
    values,
    userTimezone,
    locale,
}: {
    values: AutoCreateEventFormValues;
    userTimezone?: TimezoneDTO;
    locale?: string;
}): CreateTransientEventPayload => {
    const { endTime, dates, startTime, title } = values;
    const [start, end] = dates;
    const startDate = format(start, DEFAULT_FORMAT);
    const endDate = format(end, DEFAULT_FORMAT);
    const timezone =
        values.timezone?.timezone ||
        userTimezone?.timezone ||
        DEFAULT_TIMEZONE.timezone;

    return {
        event: {
            name: title,
            start: toUTC({
                date: composeDateTime(startDate, startTime),
                timezone,
            }),
            end: toUTC({ date: composeDateTime(endDate, endTime), timezone }),
            locale: locale || 'en_US',
        },
    };
};

export const createEventServicePayload = ({
    currency,
    values,
    timezone: eventTimezone,
    locale,
    organizerId,
    venueId,
    venueName,
}: {
    currency?: string;
    values: AutoCreateEventFormValues;
    timezone?: TimezoneDTO;
    locale?: string;
    organizerId: string;
    venueId?: string;
    venueName?: string;
}): EventServicePayload => {
    const {
        endTime,
        dates,
        startTime,
        title,
        areTicketsFree,
        ticketsAvailable,
        ticketsPrice,
    } = values;
    const [start, end] = dates;

    const startDate = format(new Date(start), DEFAULT_FORMAT);
    const endDate = format(new Date(end), DEFAULT_FORMAT);
    const timezone =
        values.timezone?.timezone ||
        eventTimezone?.timezone ||
        DEFAULT_TIMEZONE.timezone;

    return {
        event: {
            name: title,
            start: toUTC({
                date: composeDateTime(startDate, startTime),
                timezone,
            }),
            end: toUTC({ date: composeDateTime(endDate, endTime), timezone }),
            locale: locale || 'en_US',
            organizer_id: organizerId,
            location_type: values.locationType,
            venue_id: venueId || null,
            venue_name: venueName || null,
        },
        ticket_class: {
            free: areTicketsFree,
            quantity_total: Number(ticketsAvailable),
            cost: toPriceWithCurrency({
                price: Number(ticketsPrice),
                currency: currency || 'USD',
            }),
        },
    };
};

export const createAccountPayload = (values: CreateAccountFormValues) => {
    const { email, password, firstName, lastName } = values;
    return {
        password,
        first_name: firstName,
        last_name: lastName,
        email,
        forward: '',
        referrer: '',
        pckg: '',
        accepted_tos_by_popup: 1,
    };
};
